import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  startTransition,
} from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import DesktopView from "../layout/DesktopView";
import PersonaCard from "./PersonaCard";
import PersonaEditor from "./PersonaEditor";
import { useToast } from "../common/ToastContext";
import { useCardSelector } from "../common/CardSelectorProvider";
import personaService from "../../services/personaService";
import { currentUserHasRole } from "../../services/userService";
import ToggleControl from "../common/ToggleControl";
import usePins from "../../hooks/usePins";
import { has } from "lodash";

const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const PersonaDashboard = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [goBack, setGoBack] = useState(null);
  const { showToast } = useToast();
  const { openCardSelector } = useCardSelector();
  const [personas, setPersonas] = useState([]);
  const [deletedMatches, setDeletedMatches] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [selectedPersona, setSelectedPersona] = useState(null);
  const [topics, setTopics] = useState([]);
  const { sortPinnedFirst } = usePins("personas");

  const getStoredCompactView = () =>
    localStorage.getItem("compactViewPersona") === "true";
  const setStoredCompactView = (isCompact) =>
    localStorage.setItem("compactViewPersona", isCompact.toString());
  const [compactView, setCompactView] = useState(getStoredCompactView);

  useEffect(() => {
    setStoredCompactView(compactView);
  }, [compactView]);

  useEffect(() => {
    const checkAccess = async () => {
      try {
        setIsAdmin(await currentUserHasRole("admin"));
      } catch (error) {
        console.error("Error checking user role:", error);
        setIsAdmin(false);
      }
    };
    checkAccess();
  }, []);

  const fetchPersonas = useCallback(async () => {
    try {
      const personas = await personaService.getAll();
      setPersonas(personas);
    } catch (error) {
      console.error("Error fetching personas:", error);
      showToast("Failed to fetch personas", "error");
    }
  }, [showToast]);

  useEffect(() => {
    fetchPersonas();
  }, [fetchPersonas]);

  useEffect(() => {
    if (searchParams.has("id") && !selectedPersona) {
      const editPersonaId = searchParams.get("id");
      const editPersona = personas.find(
        (persona) => persona.id === editPersonaId,
      );
      if (editPersona) {
        setSelectedPersona(editPersona);
        if (searchParams.has("goback")) {
          setGoBack(searchParams.get("goback"));
          setSearchParams((prevParams) => {
            const newParams = new URLSearchParams(prevParams);
            newParams.delete("goback");
            return newParams;
          });
        }
      }
    }
  }, [searchParams, personas, selectedPersona, setSearchParams]);

  const handleSavePersona = async (personaData) => {
    // Extract image-related intent from the editor payload; the backend PUT/POST
    // do not accept these fields, and image mutations run as separate API calls.
    const { portraitRemoved, portraitFile, ...personaFields } = personaData;
    try {
      let personaId = personaFields.id;
      if (personaId) {
        const response = await personaService.update(personaId, personaFields);
        if (response.status !== 201) {
          throw new Error("Failed to update persona");
        }
      } else {
        const created = await personaService.create(personaFields);
        if (!created) {
          throw new Error("Failed to create persona");
        }
        personaId = created.id;
      }

      // Metadata is now committed. Apply image changes after so that a failing
      // metadata save leaves the previously persisted image untouched.
      if (portraitRemoved) {
        await personaService.deleteImage(personaId);
      }
      if (portraitFile) {
        await personaService.uploadImage(portraitFile, personaId);
      }

      await fetchPersonas();
      handleClosePersonaEditor();
      showToast("Persona saved successfully", "info");
    } catch (error) {
      console.error("Error saving persona:", error);
      showToast(`Error saving persona: ${error}`, "error");
    }
  };

  const handleClosePersonaEditor = useCallback(() => {
    // Must commit with React Router v7's transitioned URL update, or the id effect reopens the editor.
    startTransition(() => setSelectedPersona(null));
    setSearchParams((prevParams) => {
      const newParams = new URLSearchParams(prevParams);
      newParams.delete("id");
      return newParams;
    });
    if (goBack) {
      const goto = goBack;
      setGoBack(null);
      navigate(goto + "?reload=true");
    }
  }, [goBack, navigate, setSearchParams]);

  const recoverPersona = useCallback(
    async (persona) => {
      const recovered = await personaService.recover(persona.id);
      // Mirrors the topic restore flow: the name records when it came back.
      const name = `${recovered.name} (Restored ${new Date().toLocaleString()})`;
      await personaService.update(persona.id, { ...recovered, name });
      // Drop the stale soft-deleted copy so the dashboard shows the live one.
      setDeletedMatches((prev) =>
        prev.filter((existing) => existing.id !== persona.id),
      );
      await fetchPersonas();
      showToast(`Persona restored successfully as ${name}`, "success");
    },
    [fetchPersonas, showToast],
  );

  const handleRecoverPersona = useCallback(
    async (persona) => {
      try {
        await recoverPersona(persona);
        handleClosePersonaEditor();
      } catch (error) {
        console.error("Error restoring persona:", error);
        showToast(`Error restoring persona: ${error}`, "error");
      }
    },
    [recoverPersona, handleClosePersonaEditor, showToast],
  );

  const handleDeletePersona = async (personaId) => {
    try {
      await personaService.remove(personaId);
      handleClosePersonaEditor();
      await fetchPersonas();
      showToast("Persona deleted successfully", "info");
    } catch (error) {
      console.error("Error deleting persona:", error);
      showToast(`Error deleting persona: ${error}`, "error");
    }
  };

  const handleRestoreDeleted = async () => {
    try {
      const deleted = await personaService.getDeleted();
      openCardSelector({
        items: deleted,
        CardComponent: PersonaCard,
        title: "Select a Deleted Persona to Restore",
        onSelect: async (persona) => {
          try {
            await recoverPersona(persona);
          } catch (error) {
            console.error("Error restoring persona:", error);
            showToast(`Error restoring persona: ${error}`, "error");
          }
        },
      });
    } catch (error) {
      console.error("Error fetching deleted personas:", error);
      showToast("Failed to fetch deleted personas", "error");
    }
  };

  const handleAddNewPersona = async () => {
    const randFourDigits = Math.floor(1000 + Math.random() * 9000);
    const newPersona = {
      name: `New Persona ${randFourDigits}`,
      gender: "female",
    };
    try {
      // open fake persona
      setSelectedPersona({
        channel: {
          sms_supported: true,
          voice_supported: true,
        },
        pipecat_enabled: false,
      });
      showToast("New persona created successfully", "info");
    } catch (error) {
      console.error("Error creating new persona:", error);
      showToast(`Error creating new persona: ${error}`, "error");
    }
  };

  const handlePersonaClick = (persona) => {
    setSelectedPersona(persona);
    setSearchParams((prevParams) => {
      const newParams = new URLSearchParams(prevParams);
      newParams.set("id", persona.id);
      return newParams;
    });
  };

  const handleSearch = useCallback(
    async (term) => {
      const personaId = term.trim();
      if (!isAdmin || !UUID_PATTERN.test(personaId)) {
        return;
      }
      if (
        personas.some((persona) => persona.id === personaId) ||
        deletedMatches.some((persona) => persona.id === personaId)
      ) {
        return;
      }
      try {
        const persona = await personaService.getLatest(personaId, {
          deletedOk: true,
        });
        if (persona?.deleted_at) {
          setDeletedMatches((prev) =>
            prev.some((existing) => existing.id === persona.id)
              ? prev
              : [...prev, persona],
          );
        }
      } catch (error) {
        // A search term that looks like a UUID but matches nothing is expected.
        console.debug("No persona found for id", personaId, error);
      }
    },
    [isAdmin, personas, deletedMatches],
  );

  const orderedPersonas = useMemo(
    () => sortPinnedFirst(personas, (p) => p.id),
    [personas, sortPinnedFirst],
  );

  const visiblePersonas = useMemo(
    () => [
      ...orderedPersonas,
      ...deletedMatches.filter(
        (deleted) => !personas.some((persona) => persona.id === deleted.id),
      ),
    ],
    [orderedPersonas, personas, deletedMatches],
  );

  const renderCard = useCallback(
    (props) => <PersonaCard {...props} compact={compactView} />,
    [compactView],
  );

  const renderEditor = useCallback(
    (props) => (
      <PersonaEditor
        {...props}
        topics={topics}
        onRecover={isAdmin ? handleRecoverPersona : undefined}
      />
    ),
    [topics, isAdmin, handleRecoverPersona],
  );

  const urlParams = new URLSearchParams(window.location.search);
  const hasMigrateEnabled = urlParams.has("migrate");

  return (
    <>
      <div className="absolute top-5 left-1/4 -translate-x-1/4 flex space-x-4 z-[1]">
        <ToggleControl
          offLabel="Detailed"
          onLabel="Compact"
          offIcon=""
          onIcon=""
          initialState={compactView}
          onChange={(on) => {
            setCompactView(on);
          }}
          useActiveForBothStates={true}
        />
      </div>
      <DesktopView
        items={visiblePersonas}
        selectedItem={selectedPersona}
        onSearch={handleSearch}
        searchPlaceholder="Filter personas ..."
        contextMenuItems={
          isAdmin
            ? [
                {
                  label: "Restore Deleted",
                  onClick: handleRestoreDeleted,
                },
              ]
            : []
        }
        onItemClick={handlePersonaClick}
        onSave={handleSavePersona}
        onDelete={isAdmin ? handleDeletePersona : undefined}
        onReset={() => {}} // Personas don't have a reset functionality
        onClose={handleClosePersonaEditor}
        Card={renderCard}
        Editor={renderEditor}
        addNewItem={handleAddNewPersona}
        eventName="personaCreated"
      />
    </>
  );
};

export default PersonaDashboard;
